import { FinancialServiceProviderAdapters, OAuthProvider } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { LoadingSection } from '../../design-system/loading/loading';
import { exhaustiveCheck } from '../../utils/exhaustive-check';
import { postSwrFetcher } from '../../utils/post-swr-fetcher';
import { OAuthProviderDisconnectConfirmationModal } from '../invoice-pay/components/invoice-data-section/modals/oauth-provider-disconnect-confirmation-modal';
import { XeroConnectModal } from '../invoice-pay/components/invoice-data-section/modals/xero/xero-connect-modal';
import { CONNECT_XERO_URL } from '../invoice-pay/invoice-pay-page-inner-wrapper';
import { useGmailConnection } from '../invoice-pay/utils/use-gmail-connection';
import { useXeroConnection } from '../invoice-pay/utils/use-xero-connection';
import { AccountMappingExitConfirmationModal } from './components/xero-flow-modal/account-mapping-exit-confirmation-modal';
import { ExternalBankAccount } from './components/xero-flow-modal/account-mapping-form';
import { AccountMappingModal } from './components/xero-flow-modal/account-mapping-modal';
import { IntegrationsPage } from './integrations-page';
import { OAuthConnectionInformation, OAuthConnectionStatus, OAuthProviderConnection } from './types/types';
import { isAllowedOAuthProvider } from './utils/is-allowed-oauth-provider';

const API_URL_ACCOUNTING = '/api/accounting/';

export type BankAccountMapping = {
  seapointAccountId: string; // Includes Modulr and OB accounts
  externalAccountId: string; // Xero bank accounts
  externalAccountName?: string;
  currency?: string;
  syncFromDate?: string;
};

export const IntegrationsPageWrapper = () => {
  const navigate = useNavigate();
  const { oauthProvider } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isXeroProvider = searchParams.get('provider') === 'xero';
  const isXeroSuccess = searchParams.get('success') === 'true';
  const { data, isLoading: isLoadingData, error: dataError } = useData();

  const {
    xeroConnectionStatus,
    isCheckingXeroConnection,
    isDisconnectingXero,
    mutations: { onDisconnectXero },
  } = useXeroConnection();
  const {
    gmailConnectionStatus,
    isCheckingGmailConnection,
    isDisconnectingGmail,
    mutations: { onDisconnectGmail },
  } = useGmailConnection();

  const {
    data: xeroBankAccounts,
    isLoading: isLoadingXeroBankAccounts,
    error: xeroBankAccountsError,
    mutate: mutateXeroBankAccounts,
  } = useSWR<ExternalBankAccount[], Error, string | null>(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/bank-accounts`
      : null,
  );
  const {
    data: xeroAccountMappings,
    isLoading: isLoadingXeroAccountMappings,
    error: xeroAccountMappingsError,
  } = useSWR<{ mappings: BankAccountMapping[] }, Error, string | null>(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/account-mappings`
      : null,
  );
  const { trigger: triggerXeroAccountMappings, isMutating: isMutatingXeroAccountMappings } = useSWRMutation<
    { xeroAccountId: string; message: string },
    Error,
    string | null,
    { mappings: BankAccountMapping[] }
  >(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/account-mappings`
      : null,
    postSwrFetcher,
  );
  const { trigger: triggerCreateXeroBankAccount, isMutating: isMutatingCreateXeroBankAccount } = useSWRMutation<
    { success: boolean; message: string },
    Error,
    string | null,
    { seapointAccountId: string }
  >(
    xeroConnectionStatus?.connectionId
      ? `${API_URL_ACCOUNTING}${xeroConnectionStatus?.connectionId}/create-bank-account`
      : null,
    postSwrFetcher,
  );

  const [isXeroConnectionModalOpen, setIsXeroConnectionModalOpen] = useState(false);
  const [isOAuthProviderDisconnectModalOpen, setIsOAuthProviderDisconnectModalOpen] = useState(false);
  const [isAccountMappingModalOpen, setIsAccountMappingModalOpen] = useState(() => isXeroProvider && isXeroSuccess);
  const [isAccountMappingExitConfirmationModalOpen, setIsAccountMappingExitConfirmationModalOpen] = useState(false);

  if (
    isLoadingData ||
    isCheckingXeroConnection ||
    isCheckingGmailConnection ||
    isLoadingXeroBankAccounts ||
    isLoadingXeroAccountMappings
  ) {
    return <LoadingSection text="Loading integrations..." />;
  }

  if (!data || !xeroConnectionStatus || !gmailConnectionStatus || xeroBankAccountsError || xeroAccountMappingsError) {
    return <ErrorSection error={dataError} />;
  }

  const { accounts } = data;

  const hasModulrAccounts = accounts.some((account) => account.fspAdapter === FinancialServiceProviderAdapters.MODULR);

  const connections: OAuthProviderConnection[] = [
    {
      id: OAuthProvider.GMAIL,
      status: getOAuthProviderConnectionStatus(gmailConnectionStatus),
    },
    {
      id: OAuthProvider.XERO,
      status: getOAuthProviderConnectionStatus(xeroConnectionStatus),
    },
    {
      id: 'quickbooks',
      status: 'COMING_SOON',
    },
    {
      id: 'slack',
      status: 'COMING_SOON',
    },
  ];

  const redirectUrlQueryParam = `redirectUrl=${encodeURIComponent(window.location.pathname)}`;

  const onConnectXero = async () => {
    window.location.href = `${CONNECT_XERO_URL}&${redirectUrlQueryParam}`;

    return;
  };

  const onCloseAccountMappingModal = () => {
    searchParams.delete('provider');
    searchParams.delete('success');
    setSearchParams(searchParams);

    setIsAccountMappingModalOpen(false);
  };

  const providerConfig: ComponentProps<typeof IntegrationsPage>['providerConfig'] =
    oauthProvider && isAllowedOAuthProvider(oauthProvider)
      ? {
          provider: oauthProvider,
          connectionStatus: oauthProvider === OAuthProvider.XERO ? xeroConnectionStatus : gmailConnectionStatus,
          onGoBack: () => navigate('/dashboard/integrations'),
          onDisconnect: () => setIsOAuthProviderDisconnectModalOpen(true),
        }
      : { provider: undefined };

  return (
    <>
      <IntegrationsPage
        connections={connections}
        providerConfig={providerConfig}
        onReviewInvoice={(connectionId) => {
          const status = connections.find((c) => c.id === connectionId)?.status;

          if (!status) {
            return;
          }

          switch (status) {
            case 'CONNECTED':
              navigate(`/dashboard/integrations/${connectionId}`);

              return;
            case 'DISCONNECTED':
              if (connectionId === OAuthProvider.XERO) {
                setIsXeroConnectionModalOpen(true);
                return;
              }

              if (connectionId === OAuthProvider.GMAIL) {
                navigate('/dashboard/invoice-pay?email-modal-open=true');
                return;
              }

              return;
            case 'COMING_SOON':
              return;
            default:
              return exhaustiveCheck(status);
          }
        }}
      />

      <XeroConnectModal
        isOpen={isXeroConnectionModalOpen}
        onClose={() => setIsXeroConnectionModalOpen(false)}
        onContinue={onConnectXero}
      />

      {oauthProvider === OAuthProvider.XERO && (
        <OAuthProviderDisconnectConfirmationModal
          provider={oauthProvider}
          isOpen={isOAuthProviderDisconnectModalOpen}
          isLoading={isDisconnectingXero}
          onClose={() => setIsOAuthProviderDisconnectModalOpen(false)}
          onDisconnect={async () => {
            if (!xeroConnectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to disconnect xero', {
                extra: {
                  xeroConnectionStatus,
                },
              });

              return;
            }

            try {
              await onDisconnectXero(xeroConnectionStatus?.connectionId);

              toast.success('Xero disconnected successfully');

              navigate('/dashboard/integrations');
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  error,
                  xeroConnectionStatus,
                  message: 'Failed to disconnect Xero from the integrations page',
                },
              });

              toast.error('Failed to disconnect Xero');
            }
          }}
        />
      )}

      {oauthProvider === OAuthProvider.GMAIL && (
        <OAuthProviderDisconnectConfirmationModal
          provider={oauthProvider}
          isOpen={isOAuthProviderDisconnectModalOpen}
          isLoading={isDisconnectingGmail}
          onClose={() => setIsOAuthProviderDisconnectModalOpen(false)}
          onDisconnect={async () => {
            if (!gmailConnectionStatus?.connectionId) {
              Sentry.captureMessage('No connection id found when trying to disconnect Gmail', {
                extra: {
                  gmailConnectionStatus,
                },
              });

              return;
            }

            try {
              await onDisconnectGmail(gmailConnectionStatus?.connectionId);

              toast.success('Gmail disconnected successfully');

              navigate('/dashboard/integrations');
            } catch (error) {
              Sentry.captureException(error, {
                extra: {
                  error,
                  gmailConnectionStatus,
                  message: 'Failed to disconnect Gmail from the integrations page',
                },
              });

              toast.error('Failed to disconnect Gmail');
            }
          }}
        />
      )}

      {xeroBankAccounts && xeroAccountMappings && hasModulrAccounts && (
        <AccountMappingModal
          isOpen={isAccountMappingModalOpen}
          onClose={() => setIsAccountMappingExitConfirmationModalOpen(true)}
          accounts={accounts}
          xeroBankAccounts={xeroBankAccounts}
          xeroAccountMappings={xeroAccountMappings.mappings}
          isLoading={isMutatingXeroAccountMappings}
          isCreatingXeroAccount={isMutatingCreateXeroBankAccount}
          onCreateXeroAccount={async (seapointAccountId) => {
            try {
              await triggerCreateXeroBankAccount({ seapointAccountId });
              await mutateXeroBankAccounts();

              toast.success('Xero bank account created successfully');
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to create Xero bank account');
            }
          }}
          onSubmit={async (values) => {
            try {
              const sanitizedValues = values.map(
                (value) =>
                  ({
                    seapointAccountId: value.accountId,
                    externalAccountId: value.xeroAccountId,
                    syncFromDate: value.fromDate,
                  }) satisfies BankAccountMapping,
              );

              await triggerXeroAccountMappings({ mappings: sanitizedValues });

              toast.success('Account mappings updated successfully');

              onCloseAccountMappingModal();
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to update account mappings');
            }
          }}
        />
      )}

      <AccountMappingExitConfirmationModal
        isOpen={isAccountMappingExitConfirmationModalOpen}
        onClose={() => setIsAccountMappingExitConfirmationModalOpen(false)}
        onExit={() => {
          onCloseAccountMappingModal();

          setIsAccountMappingExitConfirmationModalOpen(false);
        }}
      />
    </>
  );
};

const getOAuthProviderConnectionStatus = (
  connectionStatus: OAuthConnectionInformation | null,
): Extract<OAuthConnectionStatus, 'CONNECTED' | 'DISCONNECTED'> => {
  if (!connectionStatus) {
    return 'DISCONNECTED';
  }

  const { connected } = connectionStatus;

  switch (connected) {
    case true:
      return 'CONNECTED';
    case false:
      return 'DISCONNECTED';
    default:
      return exhaustiveCheck(connected);
  }
};
